import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  Inject,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, firstValueFrom, take } from 'rxjs';
import { Filters } from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { DialogRiepilogoLicenzeComponent } from 'src/app/page/utenti/dialog/dialog-riepilogo-licenze/dialog-riepilogo-licenze.component';
import { AutovalutazioniService } from 'src/app/services/autovalutazioni/autovalutazioni.service';
import { Azienda, AziendaService } from 'src/app/services/azienda/azienda.service';
import { LicenzeService, PromotoreLicenze } from 'src/app/services/licenze/licenze.service';
import {
  Responsabile,
  RuoliList,
  Utente,
  UtenteService,
} from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { GestioneLicenzeComponent } from '../../gestione-licenze/gestione-licenze.component';

// ─── Costanti di dominio ────────────────────────────────────────────────────

/** Ruoli che agiscono come "responsabile" di altri utenti. */
const RUOLI_RESPONSABILE = ['utente_premium_go', 'utente_premium', 'professionista'] as const;

/** Ruoli subordinati che richiedono un responsabile e un'azienda. */
const RUOLI_SUBORDINATI = [
  'collaboratore',
  'collaboratore_go',
  'utente_read_only',
  'utente_read_only_go',
] as const;

/** Ruoli che non possono accedere alla gestione licenze. */
const RUOLI_SENZA_LICENZE = [
  'collaboratore',
  'collaboratore_go',
  'utente_read_only',
  'utente_read_only_go',
] as const;

// ─── Tipi ausiliari ─────────────────────────────────────────────────────────

interface DialogData {
  utente: Utente;
  idAzienda: string;
  listaUtentiPerAzienda?: Utente[];
}

// ─── Componente ─────────────────────────────────────────────────────────────

@Component({
  selector: 'app-dialog-crea-utenti',
  templateUrl: './dialog-crea-utenti.component.html',
  styleUrls: ['./dialog-crea-utenti.component.scss'],
  standalone: false,
})
export class DialogCreaUtentiComponent implements AfterViewInit {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  @ViewChild(GestioneLicenzeComponent) gestioneLicenzeComponent!: GestioneLicenzeComponent;

  // ── Stato pubblico ────────────────────────────────────────────────────────

  public arrayRuoli: RuoliList[] = [];
  public arrayResponsabili: Responsabile[] = [];
  public arrayAziende: Azienda[] = [];
  public arrayAutovalutazioni: any[] = [];
  public selectedAzienda: string[] = [];
  public loadingResponsabile = false;

  public readonly isPermessoSoloRead: boolean =
    !this.utenteService.isPermessoAttivo('EDIT_AZIENDA') &&
    !this.utenteService.isPermessoAttivo('CREATE_AZIENDA');

  // ── Form ─────────────────────────────────────────────────────────────────

  public readonly formUtenti = new FormGroup({
    firstName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
    lastName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email, UtilityService.emailValidator],
    }),
    ruolo: new FormControl<RuoliList | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    utenteResponsabile: new FormControl<Responsabile | undefined>(undefined, {
      nonNullable: true,
    }),
    idAziende: new FormControl<string[]>([]),
    username: new FormControl<string>(''),
    attivo: new FormControl<boolean>(true),
    keyId: new FormControl<string | undefined>(undefined),
    CMSId: new FormControl<string | undefined>(undefined),
    id: new FormControl<string | undefined>(undefined),
    codiceLicenza: new FormControl<string | undefined>(undefined, { nonNullable: true }),
    specificUser: new FormControl<boolean>(false),
    idAutovalutazione: new FormControl<string[]>([]),
  });

  // ── Signals ───────────────────────────────────────────────────────────────

  readonly specificUserSignal = toSignal(this.formUtenti.controls.specificUser.valueChanges, {
    initialValue: this.formUtenti.controls.specificUser.value,
  });

  readonly showSelectionAssessment = computed(() => this.specificUserSignal() ?? false);

  // ── Stato privato ─────────────────────────────────────────────────────────

  private ruoloScelto = '';
  private idUtenteSalvato: string | undefined;

  // ─────────────────────────────────────────────────────────────────────────

  constructor(
    public readonly dialogRef: MatDialogRef<DialogCreaUtentiComponent>,
    public readonly utenteService: UtenteService,
    public readonly aziendaService: AziendaService,
    public readonly activatedRoute: ActivatedRoute,
    public readonly licenzeService: LicenzeService,
    private readonly utilityService: UtilityService,
    private readonly dialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly autovalutazioneService: AutovalutazioniService,
    @Inject(MAT_DIALOG_DATA) public readonly data: DialogData,
  ) {
    // Carica le autovalutazioni solo quando l'utente diventa "specifico"
    effect(() => {
      if (this.specificUserSignal()) {
        this._loadAutovalutazioni();
      } else {
        this.formUtenti.get('idAutovalutazione')?.setValue([]);
      }
    });
  }

  // ─── Getters pubblici ────────────────────────────────────────────────────

  get showSpecific(): boolean {
    return this.ruoloScelto === 'professionista';
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  async ngAfterViewInit(): Promise<void> {
    this._initStorageDefault();
    this._initRuoloScelto();
    this._disableLicenzeFormInModifica();
    this._addValidatoriPerCollaboratoreLogin();

    this.ctrDisableCampi();
    await this._loadRuoli();

    if (!this.data?.listaUtentiPerAzienda?.length) {
      this._loadUtentiPerCollaboratore(this.data.utente?.ruolo?.roleName);
    }

    this.datiIniziali();

    if (this.data?.listaUtentiPerAzienda?.length && !this.data?.utente?.id) {
      this._initNuovoUtenteDaAzienda();
    }

    this.cdRef.detectChanges();
  }

  // ─── Handler pubblici (chiamati dal template) ────────────────────────────

  /**
   * Reagisce al cambio di ruolo nel select.
   */
  onSelectionChangeRuolo(event: MatSelectChange): void {
    this.ruoloScelto = event.value.roleName;
    const specificUser = this.formUtenti.get('specificUser')?.value;

    if (this.ruoloScelto !== 'professionista' && specificUser) {
      this.formUtenti.get('specificUser')?.setValue(false);
    }

    this.gestioneformLicenze();

    this._handleAziendePerRuolo(event);
    this._handleResponsabilePerRuolo();
    this._handleValidatoriPerRuolo(event);

    this.formUtenti.get('utenteResponsabile')?.updateValueAndValidity();
    this.formUtenti.get('idAziende')?.updateValueAndValidity();
  }

  /**
   * Reagisce al cambio di responsabile nel select (ruolo collaboratore).
   */
  onSelectionChangeResponsabile(event: MatSelectChange): void {
    const filtroAziende = this._buildFiltroAziende(
      event.value.idAziende,
      event.value.ruolo.roleName,
      this.ruoloScelto,
    );
    this._loadAziendeView(filtroAziende);
  }

  /**
   * Salva l'utente e, se `keepOpen` è false, chiude il dialog.
   */
  async salvaUtente(keepOpen = false): Promise<boolean> {
    if (!(await this.salva())) return false;

    this.utenteService.aggiornaInfoUtente().subscribe();

    if (!keepOpen) {
      this.dialogRef.close();
    }
    return false;
  }

  /**
   * Valida il form e persiste l'utente (creazione o aggiornamento).
   */
  async salva(): Promise<boolean> {
    if (!this._isFormValid()) return false;

    this._clearResponsabileSeNonCollaboratore();

    this.spinnerOver.show();

    const payload = {
      ...this.formUtenti.getRawValue(),
      infoLicenza:
        this.gestioneLicenzeComponent?.getObjLicenze(this.formUtenti.getRawValue().id ?? null) ??
        null,
    };

    const isModifica = !!payload.keyId;
    return isModifica ? this._aggiornaUtente(payload) : this._creaUtente(payload);
  }

  /**
   * Comparator per il select dei ruoli.
   */
  compareRuoli(ruolo1: RuoliList, ruolo2: RuoliList): boolean {
    return ruolo1?.roleName === ruolo2?.roleName;
  }

  /**
   * Comparator per il select del responsabile.
   */
  compareResponsabile(resp1: Responsabile, resp2: Responsabile): boolean {
    return resp1?.keyId === resp2?.keyId;
  }

  /**
   * Popola il form con i dati dell'utente esistente e carica le risorse correlate.
   */
  public datiIniziali(): void {
    if (this.data?.utente) {
      this._popolaFormDaUtente(this.data.utente);
    }

    if (
      this.data?.utente?.ruolo?.roleName &&
      (!this.data?.listaUtentiPerAzienda?.length || this.data?.utente?.id)
    ) {
      this._loadUtentiPerCollaboratore(this.data.utente.ruolo.roleName);
    }

    const ruolo = this.formUtenti.get('ruolo')?.value;
    if (ruolo && this._isRuoloSubordinato(ruolo.roleName)) {
      this.formUtenti.get('idAziende')?.addValidators(Validators.required);
      this.formUtenti.get('idAziende')?.updateValueAndValidity();
    }

    this._logicaGetAziende();
  }

  /**
   * Determina se il campo "responsabile" va mostrato nel template.
   */
  public mostraResponsabileHTML(): boolean {
    const isCollaboratore = this._isCollaboratore(this.ruoloScelto);
    const isReadOnly = this._isReadOnly(this.ruoloScelto);
    const isConfiguratoreLogin =
      this.utenteService.infoUtente?.utente?.ruolo?.roleName === 'configuratore';
    const isContestoDaAzienda = !!this.data?.listaUtentiPerAzienda?.length;

    const isResponsabilePresente =
      this.data.listaUtentiPerAzienda?.some((u) =>
        RUOLI_RESPONSABILE.includes(u.ruolo?.roleName as any),
      ) &&
      (isCollaboratore || isReadOnly);

    return (
      (isCollaboratore && isConfiguratoreLogin && !isContestoDaAzienda) ||
      (isContestoDaAzienda && !isReadOnly && !!isResponsabilePresente)
    );
  }

  /**
   * Controlla se il ruolo nel select va disabilitato.
   */
  ctrDisable(ruolo: string): boolean {
    const ruoloUtenteLogin = this.utenteService.infoUtente?.utente?.ruolo?.roleName;
    const ruoloUtenteInModifica = this.data?.utente?.ruolo?.roleName;
    const isModifica = !!this.formUtenti.get('id')?.value;

    if (
      isModifica &&
      (this._isCollaboratore(ruoloUtenteInModifica) || this._isCollaboratore(ruolo))
    ) {
      return true;
    }

    if (ruolo === ruoloUtenteLogin && ruoloUtenteLogin !== 'configuratore') {
      return true;
    }

    return false;
  }

  /**
   * Disabilita o abilita i campi del form in base al contesto (modifica/permessi).
   */
  ctrDisableCampi(): void {
    if (!this.data?.utente) return;

    const ruoloLogin = this.utenteService.infoUtente?.utente?.ruolo?.roleName;
    const keyIdLogin = this.utenteService.infoUtente?.utente?.keyId;
    const { id: idUtenteEdit, keyId: keyIdUtenteEdit, ruolo: ruoloUtenteEdit } = this.data.utente;

    // Utente che modifica se stesso (non configuratore)
    if (keyIdLogin === keyIdUtenteEdit && ruoloLogin !== 'configuratore') {
      this.formUtenti.get('ruolo')?.disable();
      this.formUtenti.get('idAziende')?.disable();
      return;
    }

    // Stesso ruolo ma utente diverso
    if (keyIdLogin !== keyIdUtenteEdit && ruoloLogin === ruoloUtenteEdit?.roleName) {
      this.formUtenti.disable();
    }

    if (this.isPermessoSoloRead) {
      this.formUtenti.disable();
    }

    // Collaboratore che cerca di modificare un responsabile
    if (this._isCollaboratore(ruoloLogin)) {
      if (RUOLI_RESPONSABILE.includes(ruoloUtenteEdit?.roleName as any)) {
        this.formUtenti.disable();
      }
    }

    if (idUtenteEdit) {
      this.formUtenti.get('ruolo')?.disable();
    }
  }

  /**
   * Determina se la sezione licenze va mostrata.
   */
  canSeeLicenze(): boolean {
    const isResponsabilePresente = this.data.listaUtentiPerAzienda?.some((u) =>
      RUOLI_RESPONSABILE.includes(u.ruolo?.roleName as any),
    );
    const ruoloAbilitato =
      this.ruoloScelto && !RUOLI_SENZA_LICENZE.includes(this.ruoloScelto as any);
    const isAbilitaLicenze = this.utenteService.isPermessoAttivo('ABILITA_LICENZA');

    return isAbilitaLicenze && (!!ruoloAbilitato || !this.ruoloScelto) && !isResponsabilePresente;
  }

  /**
   * Aggiorna il form licenze in base al ruolo selezionato.
   */
  gestioneformLicenze(): void {
    if (!this.canSeeLicenze()) return;

    setTimeout(() => {
      const formLicenze = this.gestioneLicenzeComponent?.formLicenze;

      if (this.ruoloScelto === 'utente_premium_go') {
        formLicenze.get('nrVSME')?.setValue(1);
        formLicenze.get('nrMaxAziende')?.setValue(1);
        this._impostaCodicePomotoreGo();
      } else {
        formLicenze.get('codicePromotore')?.enable();
        formLicenze.reset();
      }

      const dataAttivazione = new Date();
      formLicenze.get('dataAttivazione')?.setValue(dataAttivazione);
      formLicenze
        .get('dataScadenza')
        ?.setValue(this.gestioneLicenzeComponent.calcolaDataScadenza(dataAttivazione));
      formLicenze.updateValueAndValidity();
    }, 0);
  }

  /**
   * Apre il dialog di riepilogo licenze aggiuntive.
   */
  public async gestioneRiepilogoLicenze(): Promise<void> {
    await this.salvaUtente(true);

    this.dialog.open(DialogRiepilogoLicenzeComponent, {
      data: { idUtente: this.data?.utente?.id },
      panelClass: ['dialog-container', 'maxwidth'],
      disableClose: false,
      width: '95%',
      height: '95%',
      maxWidth: '95%',
      autoFocus: false,
    });
  }

  // ─── Metodi privati – inizializzazione ───────────────────────────────────

  /** Imposta il valore iniziale dello storage solo in creazione. */
  private _initStorageDefault(): void {
    if (!this.data?.utente?.id) {
      this.gestioneLicenzeComponent?.formLicenze?.get('storage')?.setValue(5);
    }
  }

  /** Sincronizza `ruoloScelto` con il ruolo dell'utente in modifica. */
  private _initRuoloScelto(): void {
    if (this.data?.utente?.ruolo?.roleName) {
      this.ruoloScelto = this.data.utente.ruolo.roleName;
    }
  }

  /** In modifica, disabilita i campi licenza base ma tiene abilitati quelli modificabili. */
  private _disableLicenzeFormInModifica(): void {
    if (this.data?.utente?.id && this.canSeeLicenze()) {
      const formLicenze = this.gestioneLicenzeComponent.formLicenze;
      formLicenze.disable();
      formLicenze.get('dataScadenza')?.enable();
      formLicenze.get('integrazioneIA')?.enable();
      formLicenze.get('rinnovoAutomatico')?.enable();
    }
  }

  /** Aggiunge i validatori obbligatori se il ruolo di login è collaboratore. */
  private _addValidatoriPerCollaboratoreLogin(): void {
    const ruoloLogin = this.utenteService.infoUtente?.utente?.ruolo?.roleName;
    if (ruoloLogin && this._isCollaboratore(ruoloLogin)) {
      this.formUtenti.get('idAziende')?.addValidators(Validators.required);
      this.formUtenti.get('utenteResponsabile')?.addValidators(Validators.required);
      this.formUtenti.get('idAziende')?.updateValueAndValidity();
      this.formUtenti.get('utenteResponsabile')?.updateValueAndValidity();
    }
  }

  /**
   * Inizializza il form per la creazione di un utente dall'interno di un'azienda.
   * Preseleziona azienda e responsabile e filtra i ruoli disponibili.
   */
  private async _initNuovoUtenteDaAzienda(): Promise<void> {
    this.formUtenti.get('idAziende')?.setValue([this.data.idAzienda]);

    const utenteResponsabile = this.data.listaUtentiPerAzienda?.find((u) =>
      RUOLI_RESPONSABILE.includes(u.ruolo.roleName as any),
    );

    const responsabile: Responsabile = {
      id: utenteResponsabile?.id,
      idAziende: utenteResponsabile?.idAziende,
      keyId: utenteResponsabile?.keyId,
      nomeUtente: `${utenteResponsabile?.firstName} ${utenteResponsabile?.lastName}`,
      ruolo: utenteResponsabile?.ruolo,
    };

    this._loadUtentiPerCollaboratore(responsabile.ruolo?.roleName);

    const aziende = await this._getAziendaByIdAzienda();

    if (responsabile.id) {
      // Esiste un responsabile → permetto solo collaboratore(_go) o read-only(_go)
      this.formUtenti.get('utenteResponsabile')?.setValue(responsabile);
      this.formUtenti.get('utenteResponsabile')?.disable();

      this.arrayRuoli = this.arrayRuoli.filter((r) =>
        this._isRuoloValidoDaAziendaConResponsabile(r, aziende?.[0]?.sosGo),
      );
    } else {
      // Nessun responsabile → permetto la creazione del responsabile stesso
      this.arrayRuoli = this.arrayRuoli.filter((r) =>
        this._isRuoloValidoDaAziendaSenzaResponsabile(r, aziende?.[0]?.sosGo),
      );
    }
  }

  /** Popola il form con i dati dell'utente esistente. */
  private _popolaFormDaUtente(utente: Utente): void {
    Object.keys(utente).forEach((key) => {
      if (key === 'utenteResponsabile') {
        this.formUtenti.get('utenteResponsabile')?.setValue(utente.utenteResponsabile);
      }
      this.formUtenti.get(key)?.setValue((utente as any)[key]);
    });

    this.formUtenti.controls.email.disable();
    this.formUtenti.controls.specificUser.disable();

    if (this.canSeeLicenze()) {
      this.gestioneLicenzeComponent.getLicenzeBase(utente.id, utente.codiceLicenza);
    }
  }

  // ─── Metodi privati – gestione ruolo ────────────────────────────────────

  /** Aggiorna la lista aziende in base al ruolo appena selezionato. */
  private _handleAziendePerRuolo(event: MatSelectChange): void {
    if (this.ruoloScelto === 'configuratore') {
      this._loadAziendeView([{ values: [this.ruoloScelto], operator: 'eq', chiave: 'ruolo' }]);
    } else if (this.ruoloScelto === 'utente_premium') {
      setTimeout(() => this.gestioneLicenzeComponent.setNrMaxAziende(1), 0);
    } else if (this.ruoloScelto === 'professionista') {
      setTimeout(() => this.gestioneLicenzeComponent.setNrMaxAziende(999), 0);
    }
  }

  /** Gestisce il campo responsabile quando cambia il ruolo. */
  private _handleResponsabilePerRuolo(): void {
    const isCollaboratore = this._isCollaboratore(this.ruoloScelto);
    const isConfiguratoreLogin =
      this.utenteService.infoUtente?.utente?.ruolo?.roleName === 'configuratore';
    const isContestoDaAzienda = !!this.data.listaUtentiPerAzienda?.length;

    if (isCollaboratore && isConfiguratoreLogin && !isContestoDaAzienda) {
      this.formUtenti.get('utenteResponsabile')?.setValue(undefined);
      this.formUtenti.get('utenteResponsabile')?.addValidators(Validators.required);
      this.formUtenti.get('idAziende')?.setValue([]);
      this._loadUtentiPerCollaboratore(this.ruoloScelto);
    } else {
      this.formUtenti.get('utenteResponsabile')?.clearValidators();
      if (!isCollaboratore) {
        this._loadAziendeGoOrCloud(this.ruoloScelto);
      }
    }
  }

  /** Aggiunge/rimuove il validatore obbligatorio su `idAziende` e filtra l'elenco. */
  private _handleValidatoriPerRuolo(event: MatSelectChange): void {
    const isSubordinato =
      this._isCollaboratore(this.ruoloScelto) || this._isReadOnly(this.ruoloScelto);
    const isContestoDaAzienda = !!this.data.listaUtentiPerAzienda?.length;

    if (isSubordinato && !isContestoDaAzienda) {
      this.formUtenti.get('idAziende')?.addValidators(Validators.required);

      const utenteLogin = this.utenteService.infoUtente?.utente;
      const filtro = this._buildFiltroAziende(
        utenteLogin?.idAziende ?? [],
        event.value?.roleName,
        this.ruoloScelto,
      );
      this._loadAziendeView(filtro);
    } else {
      this.formUtenti.get('idAziende')?.clearValidators();
    }
  }

  // ─── Metodi privati – salvataggio ────────────────────────────────────────

  /** Verifica la validità del form (e del form licenze se visibile). */
  private _isFormValid(): boolean {
    if (!this.formUtenti.valid) {
      Object.values(this.formUtenti.controls).forEach((c) => c.markAsTouched());
      return false;
    }

    if (this.canSeeLicenze() && !this.gestioneLicenzeComponent.isFormLicenzeValid()) {
      return false;
    }

    return true;
  }

  /** Se il ruolo non è collaboratore, rimuove il responsabile dal form. */
  private _clearResponsabileSeNonCollaboratore(): void {
    const ruolo = this.formUtenti.get('ruolo')?.value?.roleName;
    if (!this._isCollaboratore(ruolo)) {
      this.formUtenti.get('utenteResponsabile')?.setValue(undefined);
    }
  }

  private async _aggiornaUtente(payload: any): Promise<boolean> {
    try {
      await firstValueFrom(
        this.utenteService
          .putUtente(payload.keyId, payload)
          .pipe(finalize(() => this.spinnerOver.hide())),
      );
      this._showSuccessSnackbar('Aggiornamento effettuato');
      return true;
    } catch (err: any) {
      console.error("Errore durante l'aggiornamento dell'Utente:", err);
      this._showErrorDialog(err?.error?.message ?? "Errore durante l'aggiornamento dell'Utente");
      return false;
    }
  }

  private async _creaUtente(payload: any): Promise<boolean> {
    try {
      const esito: any = await firstValueFrom(
        this.utenteService.postUtente(payload).pipe(finalize(() => this.spinnerOver.hide())),
      );
      this.idUtenteSalvato = esito?.id;
      this._showSuccessSnackbar('Salvataggio effettuato');
      return true;
    } catch (err: any) {
      console.error("Errore durante il salvataggio dell'Utente:", err);

      // 424 → assessment creato ma mail non inviata
      if (err?.status === 424) {
        this.dialogRef.close();
      }

      this._showErrorDialog(err?.error?.message ?? "Errore nel salvataggio dell'Utente");
      return false;
    }
  }

  // ─── Metodi privati – caricamento dati ──────────────────────────────────

  private async _loadRuoli(): Promise<void> {
    try {
      this.spinnerOver.show();
      const observable = this.data?.utente?.id
        ? this.utenteService.getRuoli()
        : this.utenteService.getRuoliCreate();
      this.arrayRuoli = (await firstValueFrom(observable)) ?? [];
    } catch (err) {
      console.error('Errore durante il caricamento dei Ruoli:', err);
      this._showErrorDialog('Errore nel recupero dei Ruoli');
    } finally {
      this.spinnerOver.hide();
    }
  }

  private _loadAziendeView(filtroAzienda: Filters[] = []): void {
    this.spinnerOver.show();
    this.aziendaService
      .getAziendeView(0, 1000, '', filtroAzienda, [{ chiave: 'ragioneSociale', sort: 'asc' }])
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: (result: any) => {
          this.arrayAziende = result?.content ?? [];
          // Mantieni solo le aziende ancora presenti
          const idValidi = new Set(this.arrayAziende.map((a) => a.id));
          const idAttuali = this.formUtenti.get('idAziende')?.value ?? [];
          this.formUtenti.get('idAziende')?.setValue(idAttuali.filter((id) => idValidi.has(id)));
        },
        error: (err) => {
          console.error('Errore durante il caricamento delle aziende:', err);
          this._showErrorDialog('Errore nel recupero delle aziende');
        },
      });
  }

  private _loadAziendeGoOrCloud(ruolo: string): void {
    if (ruolo === 'configuratore') {
      this._loadAziendeView();
      return;
    }

    const tipoInstallazione = this._getTipoInstallazione(ruolo);
    const filtro: Filters[] = [
      { values: [tipoInstallazione], operator: 'eq', chiave: 'tipoInstallazione' },
    ];

    if (this._isReadOnly(ruolo)) {
      filtro.push({ values: [ruolo], operator: 'eq', chiave: 'ruolo' });
    }

    this._loadAziendeView(filtro);
  }

  private async _logicaGetAziende(): Promise<void> {
    // Caso 1: contestualizzato a una singola azienda
    if (this.data.idAzienda) {
      this._loadAziendeView([{ chiave: 'id', operator: 'eq', values: [this.data.idAzienda] }]);
      this.formUtenti.controls.idAziende.disable();
      return;
    }

    const ruolo = this.ruoloScelto;
    const tipoInstallazione = this._getTipoInstallazione(ruolo);

    // Caso 2a: l'utente ha un responsabile con aziende
    const idAziendeResponsabile = this.data?.utente?.utenteResponsabile?.idAziende;
    if (idAziendeResponsabile) {
      this._loadAziendeView([
        { chiave: 'id', operator: 'eq', values: idAziendeResponsabile },
        { chiave: 'ruolo', operator: 'eq', values: [ruolo] },
        { chiave: 'tipoInstallazione', operator: 'eq', values: [tipoInstallazione] },
      ]);
      return;
    }

    // Caso 2b: aziende globali disponibili
    const aziendeGlobali = await firstValueFrom(this.aziendaService.listaAziende);
    if (aziendeGlobali?.length) {
      this._loadAziendeView([
        { chiave: 'id', operator: 'eq', values: aziendeGlobali.map((az) => az.id) },
        { chiave: 'ruolo', operator: 'eq', values: [ruolo] },
        { chiave: 'tipoInstallazione', operator: 'eq', values: [tipoInstallazione] },
      ]);
      return;
    }

    // Caso 2c: aziende dirette dell'utente
    const idAziendeUtente = this.data?.utente?.idAziende;
    if (idAziendeUtente) {
      this._loadAziendeView([
        { chiave: 'id', operator: 'eq', values: idAziendeUtente },
        {
          chiave: 'tipoInstallazione',
          operator: 'eq',
          values: [this._getTipoInstallazione(this.data.utente.ruolo?.roleName)],
        },
      ]);
      return;
    }

    // Caso 3: fallback cloud/GO
    this._loadAziendeGoOrCloud(this.data?.utente?.ruolo?.roleName);
  }

  /**
   * Carica la lista dei potenziali responsabili per un collaboratore.
   * Filtra per tipo installazione (GO vs Cloud).
   */
  private _loadUtentiPerCollaboratore(ruolo?: string): void {
    const isGo = this._getTipoInstallazione(ruolo ?? '') === 'SOSTENIBILE_GO';

    const filtroUtenti: Filters[] = [
      { values: [true], operator: 'is', chiave: 'attivo' },
      {
        values: isGo ? ['utente_premium_go'] : ['utente_premium', 'professionista'],
        operator: 'eq',
        chiave: 'roleName',
      },
    ];

    this.loadingResponsabile = true;
    this.formUtenti.get('utenteResponsabile')?.disable();
    this.spinnerOver.show();

    this.utenteService
      .getUtenti(0, 1000, '', filtroUtenti)
      .pipe(
        finalize(() => {
          this.loadingResponsabile = false;
          if (!this.data?.listaUtentiPerAzienda?.length) {
            this.formUtenti.get('utenteResponsabile')?.enable();
          }
        }),
      )
      .subscribe({
        next: (esito) => {
          this.arrayResponsabili = esito.content.map((utente: Utente) => ({
            keyId: utente.keyId,
            id: utente.id,
            nomeUtente: `${utente.firstName} ${utente.lastName}`,
            ruolo: utente.ruolo,
            idAziende: utente.idAziende,
          }));
        },
        error: (err) => console.error(err),
      });
  }

  private async _getAziendaViewById(idAzienda: string): Promise<any> {
    try {
      this.spinnerOver.show();
      const result = await firstValueFrom(
        this.aziendaService.getAziendeView(
          0,
          1000,
          '',
          [{ values: [idAzienda], operator: 'eq', chiave: 'id' }],
          [],
        ),
      );
      return result?.content;
    } catch (error) {
      console.error('Errore durante il caricamento delle aziende:', error);
      this._showErrorDialog('Errore nel recupero delle aziende');
    } finally {
      this.spinnerOver.hide();
    }
  }

  private async _getAziendaByIdAzienda(): Promise<any> {
    try {
      this.spinnerOver.show();
      const result = await firstValueFrom(
        this.aziendaService.getAziendeByIdAzienda(
          [this.data.idAzienda],
          0,
          1000,
          '',
          [],
          [{ chiave: 'ragioneSociale', sort: 'asc' }],
        ),
      );
      return result?.content;
    } catch (error) {
      console.error('Errore durante il caricamento delle aziende:', error);
      this._showErrorDialog('Errore nel recupero delle aziende');
    } finally {
      this.spinnerOver.hide();
    }
  }

  /** Carica le autovalutazioni specifiche (lazy: eseguito una sola volta). */
  private _loadAutovalutazioni(): void {
    if (this.arrayAutovalutazioni.length > 0) return;

    this.spinnerOver.show();

    this.autovalutazioneService
      .getCfgAutovalutazioni(0, 1000, '', [
        { chiave: 'specificUser', operator: 'is', values: [true] },
        { chiave: 'stato', operator: 'eq', values: ['PUBBLICATO'] },
      ])
      .pipe(take(1))
      .subscribe({
        next: (esito) => (this.arrayAutovalutazioni = esito.content),
        error: (err) => console.error(err),
        complete: () => this.spinnerOver.hide(),
      });
  }

  // ─── Metodi privati – licenze ────────────────────────────────────────────

  /** Imposta il codice promotore SOSGO e lo disabilita. */
  private _impostaCodicePomotoreGo(): void {
    const formLicenze = this.gestioneLicenzeComponent?.formLicenze;
    const promotori = this.gestioneLicenzeComponent.arrayCodicePromotore;

    if (!promotori.length) return;

    const promotoreSosGo = promotori.find((p: PromotoreLicenze) => p.codice === 'SOSGO');
    formLicenze.get('codicePromotore')?.setValue((promotoreSosGo as PromotoreLicenze).codice);
    formLicenze.get('codicePromotore')?.disable();
  }

  // ─── Metodi privati – utility ────────────────────────────────────────────

  private _isCollaboratore(ruolo?: string): boolean {
    return ruolo === 'collaboratore' || ruolo === 'collaboratore_go';
  }

  private _isReadOnly(ruolo?: string): boolean {
    return ruolo === 'utente_read_only' || ruolo === 'utente_read_only_go';
  }

  private _isRuoloSubordinato(ruolo: string): boolean {
    return RUOLI_SUBORDINATI.includes(ruolo as any);
  }

  private _getTipoInstallazione(ruolo: string): string {
    return ruolo?.includes('_go') ? 'SOSTENIBILE_GO' : 'SOSTENIBILE_CLOUD';
  }

  /**
   * Costruisce un filtro aziende standard a partire da id, tipo installazione e ruolo.
   */
  private _buildFiltroAziende(
    idAziende: string[],
    ruoloResponsabile: string,
    ruoloTarget: string,
  ): Filters[] {
    return [
      { values: idAziende, operator: 'eq', chiave: 'id' },
      {
        values: [this._getTipoInstallazione(ruoloResponsabile)],
        operator: 'eq',
        chiave: 'tipoInstallazione',
      },
      { values: [ruoloTarget], operator: 'eq', chiave: 'ruolo' },
    ];
  }

  /**
   * Ruoli permessi quando nel contesto azienda è presente un responsabile.
   * Se SOS-GO: collaboratore_go o utente_read_only_go; altrimenti varianti cloud.
   */
  private _isRuoloValidoDaAziendaConResponsabile(ruolo: RuoliList, isSosGo: boolean): boolean {
    return isSosGo
      ? ruolo.roleName === 'collaboratore_go' || ruolo.roleName === 'utente_read_only_go'
      : ruolo.roleName === 'collaboratore' || ruolo.roleName === 'utente_read_only';
  }

  /**
   * Ruoli permessi quando nel contesto azienda NON è presente un responsabile.
   * Se SOS-GO: utente_premium_go o utente_read_only_go; altrimenti varianti cloud + professionista.
   */
  private _isRuoloValidoDaAziendaSenzaResponsabile(ruolo: RuoliList, isSosGo: boolean): boolean {
    return isSosGo
      ? ruolo.roleName === 'utente_premium_go' || ruolo.roleName === 'utente_read_only_go'
      : ruolo.roleName === 'professionista' ||
          ruolo.roleName === 'utente_premium' ||
          ruolo.roleName === 'utente_read_only';
  }

  private _showSuccessSnackbar(message: string): void {
    this.utilityService.openSnackBar(message, '', {
      duration: 2000,
      panelClass: ['success-snackbar'],
    });
  }

  private _showErrorDialog(descrizione: string): void {
    this.utilityService.openDialog(
      {
        titolo: 'Attenzione',
        descrizione,
        bottoni: [{ nome_btn: 'Chiudi' }],
      },
      500,
    );
  }
}
